"use strict";
/**
 * Single source of truth for block field names AND their default display labels.
 *
 * Every entry is a FieldDef: { name: string; label: string }
 *   - name  – the field name stored in the DB (used to build translation keys)
 *   - label – the canonical default label shown when no translation is available
 *
 * These constants are used by:
 *   1. @common/ui field templates (name: Xxx.yyy.name, label: Xxx.yyy.label)
 *   2. form-viewer-ui components  (fieldLabel(Xxx.yyy) or fieldLabel(Xxx.yyy, 'Override'))
 *
 * Adding a new translatable field:
 *   1. Add the constant here with name + label
 *   2. Use it in the @common/ui template: { name: Xxx.yyy.name, label: Xxx.yyy.label, ... }
 *   3. Use it in the viewer component: fieldLabel(Xxx.yyy)
 *   The backend will automatically generate the translation key from the DB value.
 *
 * Fields whose label varies by context (e.g. IncomeFields.institutionName which is
 * labelled "Employer Name" for employers but "Income Source" for other types) carry a
 * sensible generic default; viewer components / templates may pass an override label:
 *   fieldLabel(IncomeFields.institutionName, 'Employer Name')
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BLOCK_FIELD_LABELS = exports.EducationFields = exports.DisabilityFields = exports.HousedDeclaredDisasterFields = exports.HouseholdFleeingDomesticViolenceFields = exports.PoliceSecurityOfficerFields = exports.MilitaryVeteranFields = exports.StudentFields = exports.CitizenStatusFields = exports.GeneralQuestionFields = exports.IdTypeFields = exports.ExpenseFields = exports.AssetFields = exports.IncomeFields = exports.InstitutionAddressFields = exports.AddressFields = exports.EmergencyContactFields = exports.GuarantorFields = exports.VehicleFields = exports.PetFields = exports.CoApplicantFields = exports.AlternateNameFields = exports.LeadSourceFields = exports.PersonalInformationFields = void 0;
exports.buildBlockFieldLabelMap = buildBlockFieldLabelMap;
exports.getBlockFieldLabel = getBlockFieldLabel;
exports.PersonalInformationFields = {
    firstName: { name: 'applicant_name_first', label: 'First Name' },
    lastName: { name: 'applicant_name_last', label: 'Last Name' },
    middleName: { name: 'applicant_name_middle', label: 'Middle Name' },
    maternalLastName: {
        name: 'applicant_name_maternal_last',
        label: 'Maternal Last Name',
    },
    email: { name: 'applicant_email_address', label: 'Email' },
    dateOfBirth: { name: 'applicant_birth_date', label: 'Date of Birth' },
    phoneNumber: { name: 'applicant_primary_number', label: 'Phone Number' },
    title: { name: 'applicant_title', label: 'Title' },
    occupation: { name: 'applicant_occupation', label: 'Occupation' },
    legalGender: { name: 'applicant_legal_gender', label: 'Legal Gender' },
    genderIdentity: {
        name: 'applicant_gender_identity',
        label: 'Gender Identity',
    },
    pronouns: { name: 'applicant_pronouns', label: 'Pronouns' },
};
exports.LeadSourceFields = {
    leadSource: {
        name: 'applicant_lead_source',
        label: 'How Did You Hear About Us?',
    },
};
exports.AlternateNameFields = {
    firstName: { name: 'alternate_first_name', label: 'Alternate First Name' },
    middleName: { name: 'alternate_middle_name', label: 'Alternate Middle Name' },
    lastName: { name: 'alternate_last_name', label: 'Alternate Last Name' },
};
exports.CoApplicantFields = {
    ageOver17: {
        name: 'coapplicant_age_over_17',
        label: 'Is this occupant 18 years of age or older?',
    },
    firstName: { name: 'coapplicant_first_name', label: 'First Name' },
    middleName: { name: 'coapplicant_middle_name', label: 'Middle Name' },
    lastName: { name: 'coapplicant_last_name', label: 'Last Name' },
    email: { name: 'coapplicant_email', label: 'Email' },
    phoneNumber: { name: 'coapplicant_phone_number', label: 'Phone Number' },
    dateOfBirth: { name: 'coapplicant_date_of_birth', label: 'Date of Birth' },
    minorDateOfBirth: {
        name: 'date_of_birth',
        label: 'Show date of birth for occupants under 18 years of age',
    },
    relationshipToApplicant: {
        name: 'relationship_to_applicant',
        label: 'Relationship to Applicant',
    },
    sendAppToCoApplicant: {
        name: 'send_app_to_coapplicant',
        label: 'Send App to Co-Applicant',
    },
    relationship: { name: 'relationship', label: 'Relationship' },
    allowPrimaryEnterCoapplicantInfo: {
        name: 'allow_primary_enter_coapplicant_info',
        label: 'Allow Primary to fill out form for co-applicants',
    },
};
exports.PetFields = {
    assistanceAnimal: {
        name: 'applicant_pet_assistance_animal',
        label: 'My pet is an assistance animal',
    },
    petType: { name: 'applicant_pet_type', label: 'Pet Type' },
    petName: { name: 'applicant_pet_name', label: 'Pet Name' },
    color: { name: 'applicant_pet_color', label: 'Color' },
    breed: { name: 'applicant_pet_breed', label: 'Breed' },
    weight: { name: 'applicant_pet_weight', label: 'Weight' },
    age: { name: 'applicant_pet_age', label: 'Age' },
    gender: { name: 'applicant_pet_gender', label: 'Pet Gender' },
    spayedOrNeutered: {
        name: 'applicant_pet_spayed_or_neutered',
        label: 'Has your pet been spayed or neutered?',
    },
    dateSpayedOrNeutered: {
        name: 'applicant_pet_date_spayed_or_neutered',
        label: 'Date your pet was spayed or neutered',
    },
    dateRabiesShot: {
        name: 'applicant_pet_date_rabies_shot',
        label: 'Date of last rabies shot',
    },
    licenseNumber: {
        name: 'applicant_pet_license_number',
        label: 'License Number',
    },
    licenseCity: { name: 'applicant_pet_license_city', label: 'License City' },
    houseBroken: { name: 'applicant_pet_house_broken', label: 'House Broken' },
    ownerName: { name: 'applicant_pet_owner_name', label: 'Owner Name' },
};
exports.VehicleFields = {
    make: { name: 'make', label: 'Make' },
    model: { name: 'model', label: 'Model' },
    color: { name: 'color', label: 'Color' },
    year: { name: 'year', label: 'Year' },
    licensePlateNumber: { name: 'license_plate_number', label: 'Plate Number' },
    countryCode: { name: 'country_code', label: 'Country' },
    stateCode: { name: 'state_code', label: 'State' },
};
exports.GuarantorFields = {
    relationshipToApplicant: {
        name: 'relationship_to_applicant',
        label: 'Relationship to Applicant',
    },
    firstName: { name: 'guarantor_first_name', label: 'First Name' },
    middleName: { name: 'guarantor_middle_name', label: 'Middle Name' },
    lastName: { name: 'guarantor_last_name', label: 'Last Name' },
    email: { name: 'guarantor_email', label: 'Email' },
    phoneNumber: { name: 'guarantor_phone_number', label: 'Phone Number' },
    dateOfBirth: { name: 'guarantor_date_of_birth', label: 'Date of Birth' },
    addressLine1: { name: 'guarantor_address_line_1', label: 'Address Line 1' },
    addressLine2: { name: 'guarantor_address_line_2', label: 'Address Line 2' },
    addressCity: { name: 'guarantor_address_city', label: 'City' },
    addressCountry: { name: 'guarantor_address_country', label: 'Country' },
    addressState: { name: 'guarantor_address_state', label: 'State' },
    addressZipCode: { name: 'guarantor_address_zip_code', label: 'Zip Code' },
    personalReference1: {
        name: 'personal_reference_1',
        label: 'Personal Reference #1',
    },
    personalReference2: {
        name: 'personal_reference_2',
        label: 'Personal Reference #2',
    },
};
exports.EmergencyContactFields = {
    firstName: { name: 'contact_first_name', label: 'Contact First Name' },
    lastName: { name: 'contact_last_name', label: 'Contact Last Name' },
    relationship: { name: 'contact_relationship', label: 'Contact Relationship' },
    age: { name: 'contact_age', label: 'Contact Age' },
    phoneNumber: { name: 'contact_phone_number', label: 'Contact Phone Number' },
    email: { name: 'contact_email', label: 'Contact Email' },
    country: { name: 'contact_country', label: 'Contact Country' },
    addressLine1: { name: 'contact_address_1', label: 'Contact Address Line 1' },
    addressLine2: { name: 'contact_address_2', label: 'Contact Address Line 2' },
    addressLine3: { name: 'contact_address_3', label: 'Contact Address Line 3' },
    city: { name: 'contact_city', label: 'Contact City' },
    state: { name: 'contact_state', label: 'Contact State' },
    zip: { name: 'contact_zip', label: 'Contact Zip Code' },
    hasAccessToUnit: {
        name: 'has_access_to_unit',
        label: 'Has Access to Unit',
    },
    notes: { name: 'contact_notes', label: 'Notes' },
};
/** Shared fields for current-address and previous-address blocks */
exports.AddressFields = {
    streetLine1: { name: 'street_line1', label: 'Street Line 1' },
    streetLine2: { name: 'street_line2', label: 'Street Line 2' },
    streetLine3: { name: 'street_line3', label: 'Street Line 3' },
    city: { name: 'city', label: 'City' },
    countryCode: { name: 'country_code', label: 'Country' },
    stateCode: { name: 'state_code', label: 'State' },
    zipCode: { name: 'zip_code', label: 'Zip Code' },
    residenceType: { name: 'residence_type', label: 'Residence Type' },
    leavingReason: { name: 'leaving_reason', label: 'Reason for Leaving' },
    ownershipStatus: { name: 'ownership_status', label: 'Owner/Renter' },
    communityName: { name: 'community_name', label: 'Community Name' },
    managerFirstName: { name: 'manager_first_name', label: 'Manager First Name' },
    managerLastName: { name: 'manager_last_name', label: 'Manager Last Name' },
    managerPhoneNumber: {
        name: 'manager_phone_number',
        label: 'Manager Phone Number',
    },
    managerFaxNumber: { name: 'manager_fax_number', label: 'Manager Fax Number' },
    managerEmail: { name: 'manager_email', label: 'Manager Email' },
    moveInDate: { name: 'move_in_date', label: 'Move-in Date' },
    moveOutDate: { name: 'move_out_date', label: 'Move-out Date' },
    monthlyPaymentAmount: {
        name: 'monthly_payment_amount',
        label: 'Monthly Payment Amount',
    },
    paymentRecipient: { name: 'payment_recipient', label: 'Payment Recipient' },
};
/**
 * Institution address sub-fields used inside income, asset, and expense blocks.
 * These are prefixed with "institution_" to avoid colliding with top-level
 * address block fields when both appear on the same page.
 */
exports.InstitutionAddressFields = {
    streetLine1: { name: 'institution_street_line1', label: 'Address Line 1' },
    streetLine2: { name: 'institution_street_line2', label: 'Address Line 2' },
    streetLine3: { name: 'institution_street_line3', label: 'Address Line 3' },
    city: { name: 'institution_city', label: 'City' },
    countryCode: { name: 'institution_country_code', label: 'Country' },
    stateCode: { name: 'institution_state_code', label: 'State' },
    postalCode: { name: 'institution_postal_code', label: 'Zip Code' },
};
/**
 * Shared fields used across conventional and affordable income types.
 * Several fields have context-dependent labels (e.g. institutionName is
 * "Employer Name" for employers but "Income Source" for other types).
 * Pass an override label when the generic default is not appropriate:
 *   fieldLabel(IncomeFields.institutionName, 'Employer Name')
 */
exports.IncomeFields = {
    amount: { name: 'amount', label: 'Amount' },
    institutionName: { name: 'institution_name', label: 'Institution Name' },
    frequencyOfOtherIncome: {
        name: 'frequency_of_other_income',
        label: 'Frequency of Other Income',
    },
    description: { name: 'description', label: 'Description' },
    contactName: { name: 'contact_name', label: 'Contact Name' },
    contactPhone: { name: 'contact_phone_number', label: 'Contact Phone' },
    contactFax: { name: 'contact_fax_number', label: 'Contact Fax' },
    contactEmail: { name: 'contact_email', label: 'Contact Email' },
    dateStarted: { name: 'date_started', label: 'Start Date' },
    dateEnded: { name: 'date_ended', label: 'End Date' },
    documentUpload: { name: 'document_upload', label: 'Upload Proof of Income' },
    positionOrTitle: { name: 'position_or_title', label: 'Job Title' },
    socialSecurityClaimNumber: {
        name: 'social_security_claim_number',
        label: 'Social Security Claim Number',
    },
    memberReceivesOvertimePay: {
        name: 'member_receives_overtime_pay',
        label: 'Do you receive overtime pay for this income type?',
    },
    memberReceivesBonuses: {
        name: 'member_receives_bonuses',
        label: 'Do you receive bonuses for this income type?',
    },
    memberReceivesTips: {
        name: 'member_receives_tips',
        label: 'Do you receive tips for this income type?',
    },
    typeOfBusiness: { name: 'type_of_business', label: 'Type of Business' },
    businessTaxId: { name: 'business_tax_id', label: 'Business Tax ID' },
    hourPerWeek: { name: 'hour_per_week', label: 'Hours Per Week' },
    weekPerYear: { name: 'week_per_year', label: 'Weeks Per Year' },
    frequencyId: { name: 'frequency_id', label: 'Frequency' },
};
/** Shared fields used across conventional and affordable asset types */
exports.AssetFields = {
    institutionName: { name: 'institution_name', label: 'Institution Name' },
    contactName: { name: 'contact_name', label: 'Contact Name' },
    contactPhone: { name: 'contact_phone_number', label: 'Contact Phone' },
    contactFax: { name: 'contact_fax_number', label: 'Contact Fax' },
    contactEmail: { name: 'contact_email', label: 'Contact Email' },
    description: { name: 'description', label: 'Description' },
    nameOnAccount: { name: 'name_on_account', label: 'Name on the Account' },
    routingNumber: { name: 'routing_number', label: 'Routing Number' },
    accountNumber: { name: 'account_number', label: 'Account Number' },
    marketValue: { name: 'market_value', label: 'Market Value' },
    documentUpload: { name: 'document_upload', label: 'Upload Proof of Assets' },
};
/** Shared fields used across expense types */
exports.ExpenseFields = {
    description: { name: 'description', label: 'Description' },
    institutionName: { name: 'institution_name', label: 'Provider Name' },
    contactName: { name: 'contact_name', label: 'Contact Name' },
    contactPhone: { name: 'contact_phone_number', label: 'Contact Phone' },
    contactFax: { name: 'contact_fax_number', label: 'Contact Fax' },
    contactEmail: { name: 'contact_email', label: 'Contact Email' },
    dateStarted: { name: 'date_started', label: 'Start Date' },
    dateEnded: { name: 'date_ended', label: 'End Date' },
    hourlyExpense: { name: 'hourly_expense', label: 'Hourly Expense' },
    hourPerWeek: { name: 'hour_per_week', label: 'Hours Per Week' },
    weekPerYear: { name: 'week_per_year', label: 'Weeks Per Year' },
    expenseAmount: { name: 'expense_amount', label: 'Expense Amount' },
};
/**
 * Fields used across all identification type sub-blocks (DriversLicense, Passport, etc.).
 * The 'value' field label varies by ID type (e.g. "Driver License", "Passport Number").
 * Pass an override when needed: fieldLabel(IdTypeFields.value, 'Driver License')
 */
exports.IdTypeFields = {
    value: { name: 'value', label: 'ID Number' },
    issueDate: { name: 'issue_date', label: 'Issue Date' },
    expiryDate: { name: 'expiry_date', label: 'Expiration Date' },
    countryCode: { name: 'country_code', label: 'Country' },
    stateCode: { name: 'state_code', label: 'State' },
};
exports.GeneralQuestionFields = {
    wasSuedForRent: {
        name: 'was_sued_for_rent',
        label: 'Have you ever been sued for rent?',
    },
    suedForRentComments: {
        name: 'sued_for_rent_comments',
        label: 'Please provide more details',
    },
    wasSuedForDamages: {
        name: 'was_sued_for_damages',
        label: 'Have you ever been sued for damages?',
    },
    suedForDamageComments: {
        name: 'sued_for_damage_comments',
        label: 'Please provide more details',
    },
    hasBeenEvicted: {
        name: 'has_been_evicted',
        label: 'Have you ever been evicted?',
    },
    evictedComments: {
        name: 'evicted_comments',
        label: 'Please provide more details',
    },
    hasBrokenLease: {
        name: 'has_broken_lease',
        label: 'Have you ever defaulted on a lease?',
    },
    brokenLeaseComments: {
        name: 'broken_lease_comments',
        label: 'Please provide more details',
    },
    hasFelonyConviction: {
        name: 'has_felony_conviction',
        label: 'Have you ever been convicted of a felony that involved an offense against property, persons, government officials, or that involved firearms, illegal drugs, or sex or sex crimes?',
    },
    felonyConvictionComments: {
        name: 'felony_conviction_comments',
        label: 'Please provide more details',
    },
    hasPublicJudgment: {
        name: 'has_public_judgment',
        label: 'Have you ever had any public record suits, liens, judgments or repossessions?',
    },
    publicJudgmentYear: { name: 'public_judgment_year', label: 'In what year?' },
    hasBankruptcy: {
        name: 'has_bankruptcy',
        label: 'Have you ever filed for bankruptcy protection?',
    },
    bankruptcyCounty: { name: 'bankruptcy_county', label: 'County Where Filed' },
    bankruptcyYear: { name: 'bankruptcy_year', label: 'Which Year?' },
    bankruptcyStateCode: {
        name: 'bankruptcy_state_code',
        label: 'State/Province Where Filed',
    },
    hasRentersInsurance: {
        name: 'has_renters_insurance',
        label: "Do you have renter's insurance?",
    },
    insuranceProviderName: {
        name: 'insurance_provider_name',
        label: 'Provider Name',
    },
    insurancePolicyNumber: {
        name: 'insurance_policy_number',
        label: 'Policy Number',
    },
    smokes: { name: 'smokes', label: 'Do you smoke?' },
};
/** Affordable-housing citizen/SSN status block */
exports.CitizenStatusFields = {
    citizenshipTypeId: {
        name: 'applicant_subsidy_citizenship_type_id',
        label: 'What is your citizenship status?',
    },
    hasSsn: {
        name: 'applicant_has_ssn',
        label: 'Do you have a Social Security Number (SSN)?',
    },
    ssnNumber: { name: 'applicant_ssn_number', label: 'Enter your SSN' },
    ssnException: { name: 'applicant_ssn_exception', label: 'SSN Exception' },
    tracsId: {
        name: 'applicant_customer_subsidy_details_tracs_id',
        label: 'Enter TRACS ID',
    },
};
/** Affordable-housing student status block */
exports.StudentFields = {
    isStudentHigherLearning: {
        name: 'is_student_higher_learning',
        label: 'Are you a student at an institute of higher learning?',
    },
    higherLearningStatus: {
        name: 'higher_learning_status',
        label: 'Student Status',
    },
};
/** Affordable-housing US military veteran block */
exports.MilitaryVeteranFields = {
    usMilitaryVeteran: {
        name: 'us_military_veteran',
        label: 'Are you a US military veteran?',
    },
};
/** Affordable-housing police/security officer block */
exports.PoliceSecurityOfficerFields = {
    isPoliceSecurityOfficer: {
        name: 'police_security_officer',
        label: 'Are you a police/security officer?',
    },
};
/** Affordable-housing household fleeing domestic violence block */
exports.HouseholdFleeingDomesticViolenceFields = {
    householdFleeingDomesticViolence: {
        name: 'domestic_violence',
        label: 'Are any members of this household fleeing domestic violence?',
    },
};
exports.HousedDeclaredDisasterFields = {
    housedDeclaredDisaster: {
        name: 'temporarily_housed',
        label: 'Are you housed temporarily due to Presidentially Declared Disaster?',
    },
};
/** Affordable-housing disability block */
exports.DisabilityFields = {
    disabled: { name: 'disabled', label: 'Are you disabled?' },
    disabilityType: { name: 'disability_type', label: 'Disability Type' },
};
/**
 * Education block fields (general-purpose, picklist-driven).
 * Each sub-field is independently enable/disable, required/optional and
 * label-renameable in the form builder. Dropdown values are sourced from the
 * property-scoped Entrata Core customer picklist API at runtime.
 */
exports.EducationFields = {
    university: {
        name: 'education_university',
        label: 'University',
    },
    areaOfStudy: {
        name: 'education_area_of_study',
        label: 'Area of Study',
    },
    yearOfStudy: {
        name: 'education_year_of_study',
        label: 'Year of Study',
    },
};
/**
 * Builds a runtime map of { blockName -> { fieldName -> label } } derived
 * from the typed FieldDef constants above.
 *
 * This is the single source of truth for block field labels, replacing the
 * former BLOCK_FIELD_LABELS constant in block-field-labels.ts.
 *
 * This is the single source of truth replacement for the former
 * BLOCK_FIELD_LABELS in block-field-labels.ts. Block name aliases (e.g.
 * hyphenated vs underscored variants) are included so callers can look up
 * fields using either naming convention.
 */
function buildBlockFieldLabelMap() {
    var toMap = function (fields) {
        return Object.fromEntries(Object.values(fields).map(function (f) { return [f.name, f.label]; }));
    };
    var personalInfo = __assign(__assign({}, toMap(exports.PersonalInformationFields)), toMap(exports.LeadSourceFields));
    var leadSource = toMap(exports.LeadSourceFields);
    var alternateName = toMap(exports.AlternateNameFields);
    var pets = toMap(exports.PetFields);
    var vehicles = toMap(exports.VehicleFields);
    var address = toMap(exports.AddressFields);
    var coapplicant = toMap(exports.CoApplicantFields);
    var emergencyContact = toMap(exports.EmergencyContactFields);
    var income = toMap(exports.IncomeFields);
    var asset = toMap(exports.AssetFields);
    var idType = toMap(exports.IdTypeFields);
    var education = toMap(exports.EducationFields);
    return {
        'personal-information': personalInfo,
        personal_information: personalInfo,
        'lead-source': leadSource,
        lead_source: leadSource,
        'alternate-name': alternateName,
        pets: pets,
        vehicles: vehicles,
        current_address: address,
        previous_address: address,
        'current-address': address,
        'previous-address': address,
        coapplicant_block: coapplicant,
        'coapplicant-block': coapplicant,
        coapplicant: coapplicant,
        emergency_contact: emergencyContact,
        'emergency-contact': emergencyContact,
        income: income,
        asset: asset,
        assets: asset,
        'identification-type': idType,
        identification_type: idType,
        education: education,
    };
}
/**
 * Pre-built label map. Equivalent to the former BLOCK_FIELD_LABELS constant
 * in block-field-labels.ts.
 */
exports.BLOCK_FIELD_LABELS = buildBlockFieldLabelMap();
/**
 * Returns the default display label for a field within a block, or undefined
 * if the block/field combination is not known.
 *
 * Replaces getBlockFieldLabel from the former block-field-labels.ts.
 */
function getBlockFieldLabel(blockName, fieldName) {
    var _a;
    var normalizedBlockName = blockName.toLowerCase().replace(/-/g, '_');
    var labels = (_a = exports.BLOCK_FIELD_LABELS[blockName]) !== null && _a !== void 0 ? _a : exports.BLOCK_FIELD_LABELS[normalizedBlockName];
    return labels === null || labels === void 0 ? void 0 : labels[fieldName];
}
